import React from "react";
import { useSelector } from "react-redux";
import cx from "classnames";
import { useHistory } from "react-router-dom";
// eslint-disable-next-line no-restricted-imports
import { toTel } from "../utils/toTel";
// eslint-disable-next-line no-restricted-imports
import { NearbyDoctorAvailabilitySection } from "../components/NearbyDoctorAvailabilitySection";
import stylesV2 from "./NearbyOfficeCard.module.scss";
import stylesV3 from "./NearbyOfficeCardV2.module.scss";
import { Office } from "types/office";
import { PatientReturnStatus } from "types/patientReturnStatus";
import { ExamType } from "types/examType";
import {
  selectDoctorsAvailableLaterForOffice,
  selectDoctorsAvailableTodayInOffice,
  selectNextAvailableDateForOffice,
} from "features/scheduling/nearbyOfficesSlice";
import phoneIcon from "images/callIcon.svg";
import locationIcon from "images/location.svg";
import { externalRedirect } from "utils/externalRedirect";
import * as paths from "features/routing/paths";
import { getOfficeHours } from "utils/getOfficeHours";
import blueCircle from "images/blue-circle-ticker.svg";
import morningIcon from "images/morningIcon.svg";
import afternoonIcon from "images/afternoonIcon.svg";
import eveningIcon from "images/eveningIcon.svg";
import { useSchedulingABVariant } from "../utils/useSchedulingABVariant";

interface NearbyOfficeCardProps {
  nearby: Office;
  availabilitySlots: {
    morning: number;
    afternoon: number;
    evening: number;
    total: number;
  };
  patientReturnStatus: PatientReturnStatus;
  examType: ExamType;
  newDesign: string | null;
  screenSize?: number;
  useClassicStartLayout?: boolean;
}

export const NearbyOfficeCard: React.FC<NearbyOfficeCardProps> = ({
  nearby,
  availabilitySlots,
  patientReturnStatus,
  examType,
  newDesign,
  screenSize,
  useClassicStartLayout = false,
}) => {
  const history = useHistory();
  const { isV3 } = useSchedulingABVariant();
  const styles =
    isV3 && !useClassicStartLayout ? stylesV3 : stylesV2;
  const doctorsAvailableToday = useSelector((state: RootState) =>
    selectDoctorsAvailableTodayInOffice(state, nearby.id)
  );
  const doctorsAvailableLater = useSelector((state: RootState) =>
    selectDoctorsAvailableLaterForOffice(state, nearby.id)
  );
  const nextAvailableDate = useSelector((state: RootState) =>
    selectNextAvailableDateForOffice(state, nearby.id)
  );
  const onClickOffice = (officeid: string) => {
    history.push(paths.appointmentBookingStartV2({ officeId: officeid }));
  };

  const displayDistance = (distanceMiles: any) => {
    return distanceMiles
      ? parseFloat(Number(distanceMiles).toFixed(1)) + " miles"
      : "";
  };
  const showLegacyAvailability =
    useClassicStartLayout || isV3 || !newDesign;

  return (
    <>
      <div
        className={styles.nearbyContent}
        onClick={() => onClickOffice(nearby.id)}
        aria-hidden="true"
      >
        <div className={styles.contentHeader}>
          <div className={styles.miles}>
            <img src={locationIcon} alt="Location" />
            {displayDistance(nearby.distanceMiles)}
          </div>
          <div className={styles.examDetails}>
            {showLegacyAvailability && doctorsAvailableToday.length > 0 && (
              <NearbyDoctorAvailabilitySection
                doctors={doctorsAvailableToday}
                office={nearby}
                examType={examType}
                patientReturnStatus={patientReturnStatus}
                version={2}
              />
            )}
            {showLegacyAvailability && doctorsAvailableToday.length === 0 && (
              <NearbyDoctorAvailabilitySection
                date={nextAvailableDate}
                doctors={doctorsAvailableLater}
                office={nearby}
                examType={examType}
                patientReturnStatus={patientReturnStatus}
                version={2}
              />
            )}
            {!showLegacyAvailability && (
              <div className={styles.totalSlots}>
                <img
                  className={styles.iconCircle}
                  src={blueCircle}
                  alt="success"
                />{" "}
                {availabilitySlots.total > 1
                  ? availabilitySlots.total + " Openings"
                  : availabilitySlots.total + " Opening"}
              </div>
            )}
          </div>
        </div>
        <div className={styles.officeAddressContainer}>
          <div className={styles.contentLeft}>
            <div className={styles.officeName}>{nearby.displayName}</div>
            <div className={styles.officeAddress}>
              {nearby.address1} <br />
              {nearby.city}, {nearby.stateAbbr}&nbsp;{nearby.formattedZipCode}
            </div>
          </div>
          <div className={styles.contentRight}>
            <div className={styles.timings}>{getOfficeHours(nearby)}</div>
            <div className={styles.officePhone}>
              <a
                href={toTel(nearby.formattedPhone)}
                className={styles.footerPhoneLink}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={phoneIcon}
                  alt="Contact Phone"
                  className={styles.icon}
                />
                <span className={styles.phoneNumberText}>
                  {nearby.formattedPhone}
                </span>
              </a>
            </div>
            {/*nearby.supportsMedicaid && (
            <div className={styles.medicaid}>
              <img src={handbagIcon} alt="" className={styles.icon} />
              Medicaid accepted{" "}
            </div>
          )*/}
            {/*!nearby.supportsMedicaid && (
            <div className={styles.medicaid}>
              <img src={handbagIcon} alt="" className={styles.icon} />
              Medicaid not accepted{" "}
            </div>
          )*/}
          </div>
        </div>
        {!showLegacyAvailability && (
          <div className={styles.slotsContainer}>
            {availabilitySlots.morning > 0 && (
              <div className={cx(styles.slotAvailable, styles.morningSlot)}>
                <img
                  className={cx(styles.slotIcon, styles.morningSlotIcon)}
                  src={morningIcon}
                  alt="success"
                />{" "}
                {availabilitySlots.morning}
                {screenSize && screenSize < 400 && <br />}
                {availabilitySlots.morning > 1 ? " Openings" : " Opening"}
              </div>
            )}
            {availabilitySlots.afternoon > 0 && (
              <div className={cx(styles.slotAvailable, styles.afternoonSlot)}>
                <img
                  className={styles.slotIcon}
                  src={afternoonIcon}
                  alt="success"
                />{" "}
                {availabilitySlots.afternoon}
                {screenSize && screenSize < 400 && <br />}
                {availabilitySlots.afternoon > 1 ? " Openings" : " Opening"}
              </div>
            )}
            {availabilitySlots.evening > 0 && (
              <div className={cx(styles.slotAvailable, styles.eveningSlot)}>
                <img
                  className={styles.slotIcon}
                  src={eveningIcon}
                  alt="success"
                />{" "}
                {availabilitySlots.evening}
                {screenSize && screenSize < 400 && <br />}
                {availabilitySlots.evening > 1 ? " Openings" : " Opening"}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
